import React from "react";

import { Autocomplete } from "@material-ui/lab";
import {
  Box,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  makeStyles,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import { Calendar as CalendarIcon, Clock as ClockIcon } from "react-feather";
import { KeyboardDatePicker } from "@material-ui/pickers";
import { Search as SearchIcon } from "@material-ui/icons";
import { useFeature } from "flagged";

import { Learner } from "@hubcapp/types";
import TimePicker from "@ds-developer/time-picker";

import { AllowedTravelTime, LessonFormFieldsProps } from "../../types";
import { dateTimeRangeIsValid } from "../../helpers";
import {
  repeatingLessonFrequencyNumberOptions,
  repeatingLessonOptions,
  travelTimeOptions,
} from "@hubcapp/components/Calendar/components/Modals/constants";

const useStyles = makeStyles(theme => ({
  pickerInput: {
    paddingRight: 0,
  },
  costLabel: {
    fontSize: "12px",
    marginBottom: theme.spacing(1),
    color: theme.palette.common.black,
  },
  costValue: {
    color: theme.palette.action.disabled,
  },
}));

const LessonFormFields: React.FC<LessonFormFieldsProps> = ({
  learnersLoading,
  learners,
  selectedLearner,
  startDate,
  handleStartDateChange,
  startTime,
  handleStartTimeChange,
  endTime,
  handleEndTimeChange,
  test,
  handleTestChange,
  travelTime,
  handleTravelTimeChange,
  repeatingLesson,
  handleRepeatingLessonChange,
  disableRepeatingLesson,
  disableRepeatingLessonChange,
  repeatingLessonFrequencyNumber,
  handleRepeatingLessonFrequencyNumberChange,
  handleLearnerChange,
  handleSearchLearnerEvent,
  handleDateEvent,
  handleStartTimeEvent,
  handleEndTimeEvent,
  handleTestEvent,
  values,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMdScreen = useMediaQuery(theme.breakpoints.up("md"), {
    defaultMatches: true,
  });

  const rangeIsValid = dateTimeRangeIsValid(
    startDate,
    startTime,
    startDate,
    endTime,
    values.type,
  );

  const showReoccurringLessonDropdown = useFeature(
    "hubcappReoccurringLessonsEnable",
  );

  return (
    <>
      <Box marginBottom={3}>
        <Autocomplete
          id="learner"
          options={learners}
          getOptionLabel={learner =>
            `${learner.name.firstName} ${learner.name.lastName}`
          }
          fullWidth
          disabled={learnersLoading}
          value={selectedLearner || null}
          onChange={(
            _: React.ChangeEvent<Record<string, unknown>>,
            value: Pick<Learner, "id" | "name"> | null,
          ) => {
            handleLearnerChange(value);
          }}
          renderInput={params => (
            <TextField
              {...params}
              label="Search for learners"
              variant="outlined"
              fullWidth
              onClick={handleSearchLearnerEvent}
              InputProps={{
                ...params.InputProps,
                startAdornment: learnersLoading ? (
                  <CircularProgress size={14} />
                ) : (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          )}
        />
      </Box>

      <Box marginBottom={3}>
        <KeyboardDatePicker
          inputVariant="outlined"
          name="startDate"
          label="Pick a date"
          value={startDate}
          onChange={handleStartDateChange}
          onClick={handleDateEvent}
          placeholder="dd/mm/yyyy"
          format="dd/MM/yyyy"
          inputProps={{
            inputMode: "decimal",
            "data-test": "dob-picker-input",
          }}
          InputProps={{
            classes: { root: classes.pickerInput },
          }}
          fullWidth
          keyboardIcon={<CalendarIcon />}
        />
      </Box>

      <Grid container spacing={isMdScreen ? 5 : 2}>
        <Grid item xs={6}>
          <TimePicker
            name="startTime"
            label="Start time"
            value={startTime || undefined}
            minutesStep={15}
            onChange={handleStartTimeChange}
            onClick={handleStartTimeEvent}
            icon={<ClockIcon />}
            inputDataTest="lesson-start-time-picker-input"
          />
        </Grid>

        <Grid item xs={6}>
          <TimePicker
            name="endTime"
            label="End time"
            value={endTime || undefined}
            minutesStep={15}
            onChange={handleEndTimeChange}
            onClick={handleEndTimeEvent}
            icon={<ClockIcon />}
            inputDataTest="lesson-end-time-picker-input"
            isValid={Boolean(!endTime || (endTime && rangeIsValid.isValid))}
            helperText={
              (endTime && !rangeIsValid.isValid && rangeIsValid.error) || ""
            }
          />
        </Grid>
      </Grid>
      <Box marginTop={2}>
        <Grid item xs={12}>
          <FormControl variant="outlined" style={{ width: "100%" }}>
            <InputLabel htmlFor="outlined-select-travel-time">
              Travel time
            </InputLabel>
            <Select
              labelId="outlined-select-travel-time"
              data-test=""
              value={travelTime}
              onChange={handleTravelTimeChange}
              label="Travel time"
              inputProps={{
                name: "travelTime",
                id: "select-travel-time",
              }}
              disabled={false}
            >
              {Object.keys(travelTimeOptions).map((entry, i) => (
                <MenuItem key={i} value={entry}>
                  {travelTimeOptions[Number(entry) as AllowedTravelTime]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Box>
      <Box marginTop={1}>
        <FormControlLabel
          control={
            <Checkbox
              checked={test ?? false}
              onChange={(event, checked) => {
                handleTestChange(checked);
                disableRepeatingLessonChange(checked);
              }}
              onClick={handleTestEvent}
              data-test="lesson-test-checkbox"
            />
          }
          label="Is this lesson for a test?"
        />
      </Box>
      {showReoccurringLessonDropdown && (
        <>
          <Box marginTop={2}>
            <Grid item xs={12}>
              <FormControl variant="outlined" style={{ width: "100%" }}>
                <InputLabel htmlFor="outlined-select-repeating-lesson">
                  Repeating lesson
                </InputLabel>
                <Select
                  labelId="outlined-select-repeating-lesson"
                  data-test="repeating-lesson-select"
                  value={repeatingLesson}
                  onChange={handleRepeatingLessonChange}
                  label="Repeating lesson"
                  inputProps={{
                    name: "repeatingLesson",
                    id: "select-repeating-lesson",
                  }}
                  disabled={disableRepeatingLesson}
                >
                  {Object.keys(repeatingLessonOptions).map((entry, i) => {
                    const key = entry as keyof typeof repeatingLessonOptions;
                    return (
                      <MenuItem key={i} value={key}>
                        {repeatingLessonOptions[key]}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>
          </Box>
          {repeatingLesson !== "NO_REPEAT" && !disableRepeatingLesson && (
            <Box marginTop={2}>
              <Grid item xs={12}>
                <FormControl variant="outlined" style={{ width: "100%" }}>
                  <InputLabel htmlFor="outlined-select-repeating-lesson">
                    Number of lessons
                  </InputLabel>
                  <Select
                    labelId="outlined-select-repeating-lesson"
                    data-test="repeating-lesson-select"
                    value={repeatingLessonFrequencyNumber}
                    onChange={handleRepeatingLessonFrequencyNumberChange}
                    label="Number of lessons"
                    inputProps={{
                      name: "repeatingLesson",
                      id: "select-repeating-lesson",
                    }}
                    disabled={disableRepeatingLesson}
                  >
                    {repeatingLessonFrequencyNumberOptions.map(entry => (
                      <MenuItem key={entry} value={entry}>
                        {entry}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Box>
          )}
        </>
      )}
    </>
  );
};
export default LessonFormFields;
